import { Button, ConfirmationDialog, Loading, MarkdownText, Title } from 'src/components';
import { type ChangeEvent, Fragment, useCallback, useEffect, useState } from 'react';
import {
	Check as CheckIcon,
	Close as CloseIcon,
	CloudDownload as DownloadIcon,
	Info as InfoIcon,
	ChevronLeft as LeftIcon,
	ListAlt as ListIcon,
	ChevronRight as RightIcon,
} from '@material-ui/icons';
import {
	CircularProgress,
	Divider,
	Drawer,
	FormControlLabel,
	Grid,
	Grow,
	Hidden,
	Paper,
	Radio,
	RadioGroup,
	Step,
	StepLabel,
	Stepper,
	Typography,
} from '@material-ui/core';
import { type IAnswer, type ICourse, type IQuestion, type ISection, type IUserAnswer } from 'src/models';
import { useParams } from 'react-router';

import clsx from 'clsx';
import useStyles from './Review-styles';

import { type RootState, history } from 'src/store';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import answerActions from 'src/store/Answers/actions';
import courseActions from 'src/store/Courses/actions';
import moment from 'moment';

function Review() {
	// NOTE:
	// ./src/modules/Private/Courses/Course.tsx[22:8]
	const { courseId, userId }: any = useParams();
	const classes = useStyles();

	// NOTE: Ver todas las notas de ./src/modules/private/Admin.tsx
	const dispatch = useDispatch();

	const review: ICourse = useSelector((state: RootState) => state.courses.examReview);
	const isLoading = useSelector((state: RootState) => state.courses.isLoadingExam);
	const isDownloading = useSelector((state: RootState) => state.courses.isDownloadingBulk);
	const getExamReview = (courseId: number, userId: number) =>
		dispatch(courseActions.getExamReview(courseId, userId));
	const updateUserSectionAnswers = (
		userAnswers: IUserAnswer[],
		sectionId: number,
		userId: string,
		courseId: string,
	) => dispatch(answerActions.updateUserSectionAnswers(userAnswers, sectionId, userId, courseId));
	const cleanReview = () => dispatch(courseActions.setExamReview([]));

	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [currentSection, setCurrentSection] = useState<ISection>();
	const [sectionsOpen, setSectionsOpen] = useState(false);
	const isLast = isLastSection();

	useEffect(() => {
		getExamReview(courseId, userId);
	}, [courseId, userId]);

	useEffect(() => {
		if ((review?.sections?.length || 0) > 0) {
			setCurrentSection(!!review?.sections && review?.sections.length > 0 ? review?.sections[0] : []);
		}
	}, [review.id]);

	useEffect(() => {
		return () => {
			cleanReview();
		};
	}, []);

	function handleSectionManually(section: ISection) {
		setCurrentSection(section);
	}

	function isLastSection() {
		const sections: ISection[] = review.sections ?? [];
		const lastSection: ISection = sections[sections.length - 1];

		return lastSection?.id === currentSection?.id;
	}

	function handleBack() {
		const currentSectionIndex =
			review.sections?.findIndex((section: ISection) => section.id === currentSection?.id) ?? -1;

		if (review.sections && review.sections[currentSectionIndex - 1]) {
			setCurrentSection(review.sections[currentSectionIndex - 1]);
			window.scrollTo({ behavior: 'smooth', top: 0 });
		}
	}

	function handleNext() {
		if (isLast) {
			history.push('/app/courses');
		} else {
			const currentSectionIndex =
				review.sections?.findIndex((section: ISection) => section.id === currentSection?.id) ?? -1;

			if (review.sections && review.sections[currentSectionIndex + 1]) {
				setCurrentSection(review.sections[currentSectionIndex + 1]);
				window.scrollTo({ behavior: 'smooth', top: 0 });
			}
		}
	}

	function handleSingleAnswer(questionIndex: number, answerId: number) {
		const _currentSection = { ...currentSection };
		const _questions = _currentSection.questions ?? [];
		const answerIndex = _questions[questionIndex].answers?.findIndex(
			(answer: IAnswer) => answer.id == answerId,
		);

		const question = _questions[questionIndex];
		_.map(question.answers, (answer) => (answer.isSelected = false));

		if (
			answerIndex != undefined &&
			_currentSection.questions &&
			_currentSection.questions[questionIndex]
		) {
			_currentSection.questions[questionIndex].answers![answerIndex].isSelected = true;
			setCurrentSection(_currentSection);
		}
	}

	const getLabel = useCallback(
		(answer: IAnswer) => {
			if (answer.isCorrect && answer.isSelected) {
				return (
					<div className={clsx(classes.label, classes.labelCorrectSelected)}>
						<Typography> {answer.name} </Typography>

						<CheckIcon />
					</div>
				);
			} else if (answer.isCorrect && !answer.isSelected) {
				return (
					<div className={clsx(classes.label, classes.labelCorrectNoSelected)}>
						<Typography> {answer.name} </Typography>

						<InfoIcon />
					</div>
				);
			} else if (!answer.isCorrect && answer.isSelected) {
				return (
					<div className={clsx(classes.label, classes.labelIncorrectSelected)}>
						<Typography> {answer.name} </Typography>

						<CloseIcon />
					</div>
				);
			}

			return (
				<div className={classes.label}>
					<Typography> {answer.name} </Typography>

					<Fragment />
				</div>
			);
		},
		[review.id],
	);

	function saveChanges() {
		const answers: IUserAnswer[] = [];
		currentSection?.questions?.forEach((question: IQuestion) => {
			question.answers?.forEach((answer: IAnswer) => {
				if (answer.isSelected) {
					answers.push({
						answerId: answer.id,
						attempt: review.attempt,
						isCorrect: answer.isCorrect,
						questionId: question.id,
						userId,
					});
				}
			});
		});

		updateUserSectionAnswers(answers, currentSection?.id!, userId, courseId);
	}

	const sectionsContent = (
		<Stepper
			activeStep={currentSection?.id}
			className={classes.stepsContainer}
			elevation={1}
			orientation={'vertical'}
		>
			{review.sections?.map((section: ISection) => (
				<Step active color={'secondary'} key={section.id}>
					<StepLabel
						className={classes.activeSection}
						onClick={() => {
							handleSectionManually(section);
							if (sectionsOpen) setSectionsOpen(false);
						}}
					>
						{section.name}
					</StepLabel>
				</Step>
			))}
		</Stepper>
	);

	return (
		<Loading isLoading={isLoading}>
			<ConfirmationDialog
				buttonMessage={'Sí, guardar'}
				message={'Se modificará la calificación del usuario'}
				onClose={() => {
					setConfirmationOpen(false);
				}}
				onConfirm={saveChanges}
				open={confirmationOpen}
				title={'¿Deseas guardar los cambios?'}
			/>

			<Grid container>
				<Grid item xs={6}>
					<Title
						breadcrumbs={[{ label: 'Revisión', link: '/app/reviews' }, { label: '' }]}
						title={review.user || ''}
					/>
				</Grid>

				<Grid className={classes.downloadButtonContainer} item xs={6}>
					<Button
						backgroundColor={'white'}
						disabled={isDownloading}
						onClick={() => dispatch(courseActions.downloadUserExam(userId, courseId))}
						startIcon={<DownloadIcon color={'secondary'} />}
					>
						{isDownloading ? <CircularProgress size={26} /> : 'Descargar examen'}
					</Button>
				</Grid>

				<Grid item style={{ marginTop: 24 }} xs={12}>
					<div
						className={clsx(classes.header, {
							[classes.passedCard]: review.passed,
							[classes.notPassedCard]: !review.passed,
						})}
					>
						<Typography variant={'h1'}> {review.name} </Typography>

						<Typography variant={'h3'}>
							Calificación:
							<strong
								className={clsx({
									[classes.passsedColor]: review.passed,
									[classes.notPassedColor]: !review.passed,
								})}
							>
								{review.qualification}
							</strong>
						</Typography>

						<Typography variant={'h5'}>
							{' '}
							Calificación mínima: <strong> {review.minimumQualification} </strong>{' '}
						</Typography>

						<Typography variant={'h5'}>
							{' '}
							Intento: <strong> {review.attempt} </strong>{' '}
						</Typography>

						<Typography variant={'h5'}>
							{' '}
							Fecha: <strong> {moment(review.finishedAt).format('DD/MM/YYYY')} </strong>{' '}
						</Typography>
					</div>
				</Grid>

				<Grid container spacing={2} style={{ marginTop: 24 }}>
					<Grid item md={3} xs={12}>
						<Hidden mdDown>{sectionsContent}</Hidden>

						<Hidden lgUp>
							<Button
								backgroundColor={'white'}
								fullWidth
								onClick={() => {
									setSectionsOpen(true);
								}}
								startIcon={<ListIcon />}
							>
								Secciones
							</Button>

							<Drawer
								className={classes.drawer}
								classes={{
									paper: classes.drawerPaper,
								}}
								onClose={() => {
									setSectionsOpen(false);
								}}
								open={sectionsOpen}
								variant={'temporary'}
							>
								{sectionsContent}
							</Drawer>
						</Hidden>
					</Grid>

					<Grid container item md={9} spacing={2}>
						{Boolean(currentSection) && (
							<>
								{currentSection?.questions?.map(
									(question: IQuestion, questionIndex: number) => (
										<Grid item key={question.id} xs={12}>
											<Grow
												in
												style={{
													transformOrigin: '0 0 0',
												}}
												{...{
													timeout: questionIndex + 1 * 1000,
												}}
											>
												<Paper className={classes.questionCard}>
													<MarkdownText value={question.name} />

													<Divider className={classes.divider} />

													<RadioGroup
														onChange={(event: ChangeEvent<HTMLInputElement>) => {
															handleSingleAnswer(
																questionIndex,
																Number(event.target.value),
															);
														}}
														value={
															question.answers?.find(
																(answer: IAnswer) => answer.isSelected,
															)?.id || 0
														}
													>
														{question.answers?.map((answer: IAnswer) => (
															<FormControlLabel
																className={clsx({
																	[classes.disabledRadio]:
																		answer.isSelected && answer.isCorrect,
																})}
																control={
																	<Radio
																		checked={
																			answer.id ==
																			question.answers?.find(
																				(answer: IAnswer) =>
																					answer.isSelected,
																			)?.id
																		}
																	/>
																}
																key={answer.id}
																label={getLabel(answer)}
																value={answer.id}
															/>
														))}
													</RadioGroup>
												</Paper>
											</Grow>
										</Grid>
									),
								)}

								{currentSection?.id && (
									<>
										<div className={classes.saveContainer}>
											<Button
												backgroundColor={'secondary'}
												endIcon={<CheckIcon />}
												onClick={() => {
													setConfirmationOpen(true);
												}}
											>
												Guardar cambios
											</Button>
										</div>

										<div className={classes.buttons}>
											<Button
												disabled={
													review.sections?.findIndex(
														(section: ISection) =>
															section.id == currentSection.id,
													) === 0
												}
												onClick={handleBack}
												startIcon={<LeftIcon />}
												variant={'outlined'}
											>
												Anterior
											</Button>

											<Button
												endIcon={<RightIcon />}
												onClick={handleNext}
												variant={'outlined'}
											>
												{isLast ? 'Terminar' : 'Siguiente'}
											</Button>
										</div>
									</>
								)}
							</>
						)}
					</Grid>
				</Grid>
			</Grid>
		</Loading>
	);
}

export default Review;
